import { CodeBlock } from "../components/CodeBlock";
import { useMetaTags } from "../components/MetaTags";

function Endpoint({
  method,
  path,
  description,
  children,
}: {
  method: string;
  path: string;
  description: string;
  children?: React.ReactNode;
}) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-100 text-emerald-700",
    POST: "bg-blue-100 text-blue-700",
    PATCH: "bg-amber-100 text-amber-700",
    DELETE: "bg-red-100 text-red-700",
  };
  return (
    <div className="border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`text-xs font-bold px-2 py-0.5 rounded ${colors[method] || "bg-slate-100 text-slate-700"}`}
        >
          {method}
        </span>
        <code className="text-sm font-mono text-slate-800">{path}</code>
      </div>
      <p className="text-sm text-slate-600">{description}</p>
      {children}
    </div>
  );
}

export function DocsApi() {
  useMetaTags({
    title: "OmniCanvas REST API Documentation",
    description:
      "Reference for the OmniCanvas REST API — authenticate, read and write notes and folders, and sync your spatial canvas programmatically.",
    url: "https://omnicanvasnotes.com/docs/api",
  });
  return (
    <div className="pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-sm text-slate-400 mb-2">Last updated: March 7, 2026</p>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
          Public API Reference
        </h1>
        <p className="text-lg text-slate-600 mb-12">
          Programmatic access to your OmniCanvas notes, folders, tags, and web clipping. Available on the{" "}
          <a href="/pricing" className="text-indigo-600 hover:underline">
            Pro and Team plans
          </a>
          .
        </p>

        <div className="prose prose-slate max-w-none space-y-10 text-slate-600 leading-relaxed">
          {/* Auth */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Authentication</h2>
            <p>
              All API requests require an API key passed as a Bearer token. Create and manage keys
              in <strong>Settings → API Access</strong> inside the app.
            </p>
            <CodeBlock label="Header">{`Authorization: Bearer pk_live_your_key_here`}</CodeBlock>
            <p className="mt-3 text-sm">
              Keys use the <code className="text-slate-800 bg-slate-100 px-1 rounded">pk_live_</code> prefix. Keep
              your keys secret — they grant full access to your account.
            </p>
          </section>

          {/* Base URL */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Base URL</h2>
            <CodeBlock>{`https://app.omnicanvasnotes.com/api/v1`}</CodeBlock>
          </section>

          {/* Rate Limits */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Rate Limits</h2>
            <p>
              All endpoints are rate limited to <strong>100 requests per minute</strong> per user. Rate limit
              info is returned in response headers:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>
                <code className="text-slate-800 bg-slate-100 px-1 rounded">X-RateLimit-Remaining</code> — requests left in the current window
              </li>
              <li>
                <code className="text-slate-800 bg-slate-100 px-1 rounded">X-RateLimit-Reset</code> — Unix timestamp when the window resets
              </li>
            </ul>
          </section>

          {/* Response Format */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Response Format</h2>
            <p>All responses use a consistent JSON envelope:</p>
            <CodeBlock>{`{
  "data": { ... },
  "error": null,
  "meta": {
    "timestamp": "2026-03-07T12:00:00.000Z"
  }
}`}</CodeBlock>
            <p className="mt-3 text-sm">
              On error, <code className="text-slate-800 bg-slate-100 px-1 rounded">data</code> is null and{" "}
              <code className="text-slate-800 bg-slate-100 px-1 rounded">error</code> contains{" "}
              <code className="text-slate-800 bg-slate-100 px-1 rounded">{`{ code, message }`}</code>.
            </p>
          </section>

          {/* Notes */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Notes</h2>
            <div className="space-y-4">
              <Endpoint method="GET" path="/notes" description="List your notes. Supports filtering and pagination.">
                <div className="text-sm space-y-1">
                  <p className="font-medium text-slate-700">Query params:</p>
                  <ul className="list-disc pl-5 text-slate-500 space-y-0.5">
                    <li><code>limit</code> (1–100, default 50)</li>
                    <li><code>offset</code> (default 0)</li>
                    <li><code>tag</code> — filter by tag</li>
                    <li><code>folderId</code> — filter by folder</li>
                    <li><code>search</code> — search titles</li>
                  </ul>
                </div>
              </Endpoint>

              <Endpoint method="GET" path="/notes/:id" description="Get a single note with full content.">
                <div className="text-sm space-y-1">
                  <p className="text-slate-500">
                    Pass <code>?includeCanvas=true</code> to include canvas data. Encrypted notes return{" "}
                    <code>canvasData: null</code>.
                  </p>
                </div>
              </Endpoint>

              <Endpoint method="POST" path="/notes" description="Create a new note.">
                <CodeBlock label="Body">{`{
  "title": "My Note",
  "tags": ["research", "ai"],
  "folderIds": ["folder-id"],
  "isPinned": false,
  "canvasData": { ... }
}`}</CodeBlock>
                <p className="text-sm text-slate-500 mt-2">All fields are optional.</p>
              </Endpoint>

              <Endpoint method="PATCH" path="/notes/:id" description="Update a note. Only provided fields are changed.">
                <p className="text-sm text-slate-500">
                  Tags and folderIds replace existing values entirely. Encrypted notes cannot have their canvas data updated via API.
                </p>
              </Endpoint>

              <Endpoint
                method="DELETE"
                path="/notes/:id"
                description="Delete a note. Soft delete by default (moves to trash)."
              >
                <p className="text-sm text-slate-500">
                  Pass <code>?permanent=true</code> to permanently delete including version history.
                </p>
              </Endpoint>
            </div>
          </section>

          {/* Folders */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Folders</h2>
            <div className="space-y-4">
              <Endpoint method="GET" path="/folders" description="List all folders with note counts." />
              <Endpoint method="GET" path="/folders/:id" description="Get a folder and its note list." />
              <Endpoint method="POST" path="/folders" description="Create a folder.">
                <CodeBlock label="Body">{`{
  "name": "Research",
  "parentId": "optional-parent-folder-id"
}`}</CodeBlock>
              </Endpoint>
              <Endpoint method="PATCH" path="/folders/:id" description="Update a folder name or parent." />
              <Endpoint method="DELETE" path="/folders/:id" description="Delete a folder (soft delete)." />
            </div>
          </section>

          {/* Search */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Search</h2>
            <div className="space-y-4">
              <Endpoint
                method="GET"
                path="/search?q=query"
                description="Full-text search across note titles and OCR text. Returns matching excerpts."
              >
                <p className="text-sm text-slate-500">
                  Minimum 2 characters. Encrypted notes are excluded. Supports <code>limit</code> and{" "}
                  <code>offset</code> pagination.
                </p>
              </Endpoint>
            </div>
          </section>

          {/* Tags */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Tags</h2>
            <div className="space-y-4">
              <Endpoint method="GET" path="/tags" description="List all tags with usage counts, sorted by frequency." />
              <Endpoint
                method="GET"
                path="/tags/:tag/notes"
                description="List notes with a specific tag. Supports pagination."
              />
            </div>
          </section>

          {/* Export */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Export</h2>
            <div className="space-y-4">
              <Endpoint
                method="GET"
                path="/notes/:id/export?format=json"
                description="Export a note as a downloadable .excalidraw JSON file."
              >
                <p className="text-sm text-slate-500">Encrypted notes return HTTP 403.</p>
              </Endpoint>
            </div>
          </section>

          {/* Version History */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Version History</h2>
            <p className="mb-4">
              All plans include automatic version history. Continuous snapshots are kept for 72 hours,
              then consolidated into daily snapshots. Free keeps 3 dailies per note and paid plans
              keep 365. Paid plans can also create up to 10 manual checkpoints per note.
            </p>
            <div className="space-y-4">
              <Endpoint method="GET" path="/notes/:id/versions" description="List version snapshots for a note." />
              <Endpoint method="GET" path="/notes/:id/versions/:versionId" description="Get a specific version with full canvas data." />
              <Endpoint method="POST" path="/notes/:id/versions" description="Create a manual checkpoint (paid plans, max 10 per note)." />
              <Endpoint
                method="POST"
                path="/notes/:id/versions/:versionId/restore"
                description="Restore a note to a previous version. Current state is auto-snapshotted first."
              />
              <Endpoint
                method="DELETE"
                path="/notes/:id/versions/:versionId"
                description="Delete a manual checkpoint. Only manual checkpoints can be deleted."
              />
            </div>
          </section>

          {/* Web Clipping */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Web Clipping</h2>
            <div className="space-y-4">
              <Endpoint method="POST" path="/clip" description="Clip a web page or YouTube video. YouTube URLs are auto-detected.">
                <CodeBlock label="Body">{`{
  "url": "https://example.com/article",
  "mode": "text",
  "includeTranscript": false
}`}</CodeBlock>
                <div className="text-sm space-y-1 mt-2">
                  <p className="font-medium text-slate-700">Fields:</p>
                  <ul className="list-disc pl-5 text-slate-500 space-y-0.5">
                    <li><code>url</code> (required) — URL to clip</li>
                    <li><code>mode</code> — <code>"visual"</code> (screenshot + text) or <code>"text"</code> (default)</li>
                    <li><code>includeTranscript</code> — for YouTube, fetch transcript (quota-limited)</li>
                  </ul>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Returns <code>type: "youtube"</code>, <code>"visual"</code>, or <code>"webpage"</code> depending
                  on the URL and mode.
                </p>
              </Endpoint>

              <Endpoint method="GET" path="/youtube/quota" description="Check your monthly YouTube transcript quota." />

              <Endpoint method="POST" path="/youtube/transcript" description="Fetch a YouTube transcript by video ID.">
                <CodeBlock label="Body">{`{ "videoId": "dQw4w9WgXcQ" }`}</CodeBlock>
                <p className="text-sm text-slate-500 mt-2">Returns HTTP 429 if monthly quota is exceeded.</p>
              </Endpoint>
            </div>
          </section>

          {/* API Keys */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">API Key Management</h2>
            <p className="mb-4">
              These endpoints use JWT auth (your login session), not API key auth. They're used by the app
              to manage your keys.
            </p>
            <div className="space-y-4">
              <Endpoint method="POST" path="/keys" description="Create a new API key. Returns the full key once — store it securely." />
              <Endpoint method="GET" path="/keys" description="List your API keys (prefix only, not the full key)." />
              <Endpoint method="DELETE" path="/keys/:id" description="Revoke an API key. Cannot be undone." />
            </div>
          </section>

          {/* Errors */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Error Codes</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 pr-4 font-medium text-slate-700">Code</th>
                    <th className="text-left py-2 pr-4 font-medium text-slate-700">HTTP</th>
                    <th className="text-left py-2 font-medium text-slate-700">Meaning</th>
                  </tr>
                </thead>
                <tbody className="text-slate-500">
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs">UNAUTHORIZED</td>
                    <td className="py-2 pr-4">401</td>
                    <td className="py-2">Missing or invalid API key</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs">FORBIDDEN</td>
                    <td className="py-2 pr-4">403</td>
                    <td className="py-2">Not on a paid plan, or encrypted note</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs">NOT_FOUND</td>
                    <td className="py-2 pr-4">404</td>
                    <td className="py-2">Resource not found or not owned by you</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs">VALIDATION_ERROR</td>
                    <td className="py-2 pr-4">400</td>
                    <td className="py-2">Invalid request body or parameters</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs">RATE_LIMITED</td>
                    <td className="py-2 pr-4">429</td>
                    <td className="py-2">Rate limit or quota exceeded</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Example */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Quick Example</h2>
            <CodeBlock label="curl">{`curl https://app.omnicanvasnotes.com/api/v1/notes?limit=5 \\
  -H "Authorization: Bearer pk_live_your_key_here"`}</CodeBlock>
            <CodeBlock label="Response">{`{
  "data": [
    {
      "id": "abc123",
      "title": "Meeting Notes",
      "isPinned": false,
      "isEncrypted": false,
      "tags": ["work"],
      "folderIds": ["folder-1"],
      "lastModified": "2026-03-07T12:00:00.000Z"
    }
  ],
  "error": null,
  "meta": {
    "timestamp": "2026-03-07T12:00:00.000Z",
    "limit": 5,
    "offset": 0,
    "total": 42
  }
}`}</CodeBlock>
          </section>
        </div>
      </div>
    </div>
  );
}
